import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { CalendarCheck, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { formatShift } from '../utils/format';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const PAGE_SIZE = 10;

export default function AttendancePage() {
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    api.get('/outlets').then((r) => setOutlets(r.data));
    api.get('/staff').then((r) => setAllStaff(r.data));
  }, []);

  const q = search.trim().toLowerCase();
  const visibleStaff = allStaff.filter((s) => {
    if (selectedOutlet && s.outlet?._id !== selectedOutlet) return false;
    if (!q) return true;
    return [s.name, s.staffId, s.outlet?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const sortedStaff = [...visibleStaff].sort((a, b) => {
    if (sortKey === 'status') {
      const av = a.isActive === false ? 1 : 0;
      const bv = b.isActive === false ? 1 : 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return (a.isActive === false ? 1 : 0) - (b.isActive === false ? 1 : 0);
  });

  const totalPages = Math.ceil(sortedStaff.length / PAGE_SIZE);
  const pageStaff = sortedStaff.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search, selectedOutlet]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Salary & Attendance</h2>
          <p className="text-muted">Select a staff member to view and manage attendance</p>
        </div>
        <div className="header-actions">
          <select className="form-select" value={selectedOutlet} onChange={(e) => setSelectedOutlet(e.target.value)}>
            <option value="">All Outlets</option>
            {outlets.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
          </select>
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff by name, ID, outlet..."
            />
          </div>
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Staff ID</th>
              <th>Staff Name</th>
              <th>Outlet</th>
              <th>Shift</th>
              <th>Salary & Attendance</th>
              <th className="th-sortable" onClick={() => toggleSort('status')}>
                Status
                <span className="sort-carets">
                  <ChevronUp size={11} className={sortKey === 'status' && sortDir === 'asc' ? 'active' : ''} />
                  <ChevronDown size={11} className={sortKey === 'status' && sortDir === 'desc' ? 'active' : ''} />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageStaff.map((s) => (
              <tr key={s._id} className={s.isActive === false ? 'row-disabled' : ''}>
                <td><span className="badge outlet-id-badge">{s.staffId}</span></td>
                <td>
                  <div className="staff-name-cell">
                    {s.photo && <img src={`${API_BASE}/uploads/${s.photo}`} alt="" className="staff-avatar" />}
                    <strong>{s.name}</strong>
                  </div>
                </td>
                <td>{s.outlet?.name || '—'}</td>
                <td>{formatShift(s.shiftStart, s.shiftEnd)}</td>
                <td>
                  <button className="btn btn-sm btn-outline" onClick={() => navigate(`/attendance/${s._id}`)}>
                    <CalendarCheck size={13} /> View
                  </button>
                </td>
                <td>
                  <span className={`status-badge ${s.isActive === false ? 'inactive' : 'active'}`}>
                    {s.isActive === false ? 'Inactive' : 'Active'}
                  </span>
                </td>
              </tr>
            ))}
            {sortedStaff.length === 0 && (
              <tr><td colSpan={6} className="empty-state">
                {allStaff.length === 0 ? 'No staff yet.' : 'No staff match your filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {sortedStaff.length > PAGE_SIZE && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedStaff.length)}–{Math.min(page * PAGE_SIZE, sortedStaff.length)} of {sortedStaff.length}
          </span>
          <div className="pagination-controls">
            <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, idx) => p === '...'
                ? <span key={`e${idx}`} className="pagination-ellipsis">…</span>
                : <button key={p} className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>Next ›</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}
